import humanizeDuration from 'humanize-duration';

const pad = function (num: number, size: number) {
  return ('000' + num).slice(size * -1);
};

// dayjs format strings matching the luxon presets this used to return:
//  DATETIME_MED   "Aug 6, 2014, 9:42 AM"  → 'MMM D, YYYY, h:mm A'
//  DATETIME_SHORT "8/6/2014, 9:42 AM"     → 'M/D/YYYY, h:mm A'
const formatDateTime = (): string => {
  const currentLocal =
    (typeof navigator !== 'undefined' && navigator.language) || 'en';
  if (currentLocal.includes('he')) {
    return 'M/D/YYYY, h:mm A';
  }
  return 'MMM D, YYYY, h:mm A';
};

// Format a millisecond duration as hh:mm:ss with hours NOT capped at 24,
// matching the old luxon Duration.toFormat('hh:mm:ss') behaviour.
const formatDurationClock = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.trunc(ms / 1000));
  const hours = Math.trunc(totalSeconds / 3600);
  const minutes = Math.trunc(totalSeconds / 60) % 60;
  const seconds = totalSeconds % 60;
  return pad(hours, 2) + ':' + pad(minutes, 2) + ':' + pad(seconds, 2);
};

// Convert time to 00:00:00
const formatTime = (timeInSeconds: number): string => {
  const time: any = parseFloat(String(timeInSeconds)).toFixed(3),
    hours = Math.floor(time / 60 / 60),
    minutes = Math.floor(time / 60) % 60,
    seconds = Math.floor(time - minutes * 60);
  return pad(hours, 2) + ':' + pad(minutes, 2) + ':' + pad(seconds, 2);
};

// Convert amount to K/M/B
const formatNumber = (amount: number | null): string => {
  if (!amount || amount < 0) return '0';
  if (amount < 1e3) return '' + amount;
  if (amount >= 1e3 && amount < 1e6) return +(amount / 1e3).toFixed(1) + 'K';
  if (amount >= 1e6 && amount < 1e9) return +(amount / 1e6).toFixed(1) + 'M';
  if (amount >= 1e9 && amount < 1e12) return +(amount / 1e9).toFixed(1) + 'B';
  if (amount >= 1e12) return +(amount / 1e12).toFixed(1) + 'T';
  return '' + amount;
};

const toTitleCase = (phrase: string): string => {
  return phrase
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const removePunctuation = (string: string): string => {
  const punctuation = '!"#$%&\'()*+,-./:;<=>?@[\\]^_`{|}~';
  return string
    .split('')
    .filter(function (letter) {
      return punctuation.indexOf(letter) === -1;
    })
    .join('');
};

const pluralize = (count: number, noun: string, suffix = 's') =>
  `${count} ${noun}${count !== 1 ? suffix : ''}`;

const shortEnglishHumanizer = humanizeDuration.humanizer({
  language: 'shortEn',
  languages: {
    shortEn: {
      y: () => 'y',
      mo: () => 'mo',
      w: () => 'w',
      d: () => 'd',
      h: () => 'h',
      m: () => 'm',
      s: () => 's',
      ms: () => 'ms'
    }
  }
});

export {
  formatDateTime,
  formatDurationClock,
  formatNumber,
  formatTime,
  toTitleCase,
  removePunctuation,
  pad,
  pluralize,
  shortEnglishHumanizer
};
